/**
 * The suspense read behind an instance mount: a structure module and the
 * instance's active feature toggles, fetched in parallel.
 *
 * `useInstanceConfig` takes the instance's identity, its feature source, and
 * its module query; runs both queries through one `useSuspenseQueries` call,
 * which starts both fetches before suspending on either; and assembles the
 * mounted instance's config from the results. The reading locale is injected
 * here rather than named by each route layout, the same way `useSiloQuery`
 * injects it, so a features read shares its cache entry with the rest of the
 * app's reads of that resource.
 *
 * Only a space and a story have toggles of their own, and each names the
 * resource that holds them: a space the space resource, a story the stories
 * resource. Every other structure omits the source and takes its features
 * from the structure and deployment defaults `assembleInstanceConfig` reads.
 */
import { useSuspenseQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { resolveLanguageFromUrl } from 'system/request-metadata';
import type { StructureModuleQuery } from 'system/structure-modules';
import { assembleInstanceConfig, type FeaturesQueryOptions, type FeaturesSource, type InstanceConfig } from 'util/features';
import { EMPTY_OBJECT } from 'util/stdtypes';

/**
 * Stands in for the features query when a mount names no source, so the pair
 * of queries below is always a pair and the module read keeps its slot.
 *
 * `initialData` is what makes it resolved rather than fetched: a suspense query
 * holding data already never suspends, so a mount with no features to read
 * waits on its structure module alone.
 */
const RESOLVED_EMPTY_FEATURES: FeaturesQueryOptions = {
    queryKey: ['noInstanceFeatures'],
    queryFn: () => EMPTY_OBJECT as Record<string, boolean>,
    initialData: EMPTY_OBJECT as Record<string, boolean>,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
};

/**
 * Resolves everything an instance mount needs: the structure module, the
 * instance's active feature toggles, and the `config`/`screenSet` pair
 * assembled from both. The two queries fetch in parallel, and the caller
 * suspends until both are ready. Every identity resolves: an instance that
 * does not exist, or is hidden from the caller, gets the default
 * configuration, since its feature map reads as empty. A failed read throws.
 *
 * `featuresSource` is omitted by a structure with no per-instance toggles,
 * which is every structure but a space and a story.
 */
export function useInstanceConfig({
    siloKey,
    structureKey,
    instanceKey,
    featuresSource,
    moduleQuery,
}: {
    siloKey: string;
    structureKey: string;
    instanceKey: string;
    featuresSource?: FeaturesSource;
    moduleQuery: StructureModuleQuery;
}): InstanceConfig {
    const utils = useApiQueryUtils();
    const locale = resolveLanguageFromUrl();
    const [featuresResult, moduleResult] = useSuspenseQueries({
        queries: [featuresSource?.(utils, { siloKey, structureKey, instanceKey, locale }) ?? RESOLVED_EMPTY_FEATURES, moduleQuery],
    });
    const activeFeatures = featuresResult.data;
    const { structureModule } = moduleResult.data;

    return useMemo(
        () => ({
            siloKey,
            structureKey,
            instanceKey,
            structureModule,
            activeFeatures,
            ...assembleInstanceConfig({ structureModule, siloKey, structureKey, activeFeatures }),
        }),
        [structureModule, activeFeatures, siloKey, structureKey, instanceKey],
    );
}
